/* eslint-disable no-await-in-loop */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Spinner, UncontrolledTooltip, ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import useApi from 'hooks/useApi';

import { ReactComponent as ConversationIcon } from 'assets/icon-conversation.svg';
import { ReactComponent as NotificationIcon } from 'assets/icon-notification.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';

import { colors, fonts } from 'shared/defaultTheme';
import { LabelIcon } from 'shared/components/Labels';
import { ButtonLabelIcon } from 'shared/components/Buttons';
import { AddFormModal } from 'shared/components/SharedStyles/SharedStyles';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import AddNotify from 'shared/components/AddNotify';
import useNotification from 'hooks/useNotification';
import { SearchNotifyDiv, SearchClients, Ul } from './styles';

const Notifications = () => {
  const { customAreaApi } = useApi();
  const theme = useSelector((state) => state.theme);
  const { cusNavId, clientId, isCustomer, mail } = useSelector((state) => state.auth);
  const { currentClient } = useSelector((state) => state.clientState);
  const { currentNotificationReview, currentNotificationReviewId } = useSelector(
    (state) => state.notificationReview
  );
  const { showTypeNotifications } = useSelector((state) => state.notification);
  const [searchValue, setSearchValue] = useState('');
  const [openFormModal, setOpenFormModal] = useState(false);
  const { t } = useTranslation();
  const { mainColor } = theme;
  const [notifyList, setNotifyList] = useState([]);

  const { handleSelectNotificationReview, getNotificationById } = useNotification();

  const [{ loading }, getNotificationsData] = customAreaApi(
    {
      url: `${window.API_V1}/cli/notifications/${cusNavId}/search?login=${mail}`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: massiveLoading }, getNotificationsMassiveData] = customAreaApi(
    {
      url: `${window.API_V1}/gen/not/${cusNavId}/search?login=${mail}`,
      method: 'POST',
    },
    { manual: true }
  );

  const notificationsData = async () => {
    const filters = !showTypeNotifications
      ? [
          {
            model: 'Notify',
            field: 'Date',
            asc: false,
          },
          {
            model: 'Notify',
            field: 'ClientId',
            filter: [currentClient?.clientId || clientId],
          },
        ]
      : [
          {
            model: 'GeneralNotify',
            field: 'Date',
            asc: false,
          },
        ];
    if (!isCustomer) {
      if (!showTypeNotifications) {
        filters.push({
          model: 'Notify',
          field: 'Mail',
          filter: [mail],
        });
      } else {
        filters.push({
          model: 'GeneralNotify',
          field: 'Mail',
          filter: [mail],
        });
      }
    }
    const {
      data: { data },
    } = !showTypeNotifications
      ? await getNotificationsData({
          data: {
            filters,
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        })
      : await getNotificationsMassiveData({
          data: {
            filters,
            reduce: {
              limit: 0,
              skip: 0,
            },
          },
        });
    if (data.length === 0) {
      setNotifyList([]);
    }
    if (data.length > 0) {
      setNotifyList(data);
    }
    if (currentNotificationReview) {
      const notificationById = data.find((x) => x.id === currentNotificationReview.id);
      handleSelectNotificationReview(notificationById);
    }
  };

  useEffect(() => {
    (async () => {
      await notificationsData();
    })();
  }, [currentClient, clientId, showTypeNotifications, currentNotificationReviewId]);

  const onCreate = async () => {
    await notificationsData();
  };

  const searchNotifications = async (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
      try {
        const filters = !showTypeNotifications
          ? [
              {
                model: 'Notify',
                field: 'Date',
                asc: false,
              },
              {
                model: 'Notify',
                field: 'ClientId',
                filter: [currentClient?.clientId || clientId],
              },
              {
                model: 'Notify',
                field: 'Name',
                filter: [value],
                isRegex: true,
              },
            ]
          : [
              {
                model: 'GeneralNotify',
                field: 'Date',
                asc: false,
              },
              {
                model: 'GeneralNotify',
                field: 'Name',
                filter: [value],
                isRegex: true,
              },
            ];
        if (!isCustomer) {
          if (!showTypeNotifications) {
            filters.push({
              model: 'Notify',
              field: 'Mail',
              filter: [mail],
            });
          } else {
            filters.push({
              model: 'GeneralNotify',
              field: 'Mail',
              filter: [mail],
            });
          }
        }
        const {
          data: { data },
        } = !showTypeNotifications
          ? await getNotificationsData({
              data: {
                filters,
                reduce: {
                  limit: 0,
                  skip: 0,
                },
              },
            })
          : await getNotificationsMassiveData({
              data: {
                filters,
                reduce: {
                  limit: 0,
                  skip: 0,
                },
              },
            });
        if (data.length === 0) {
          setNotifyList([]);
        }
        if (data.length > 0) {
          setNotifyList(data);
        }
      } catch (error) {
        setNotifyList([]);
      }
    } else {
      setSearchValue('');
      try {
        const filters = !showTypeNotifications
          ? [
              {
                model: 'Notify',
                field: 'Date',
                asc: false,
              },
              {
                model: 'Notify',
                field: 'ClientId',
                filter: [currentClient?.clientId || clientId],
              },
              {
                model: 'Notify',
                field: 'Name',
                filter: [''],
                isRegex: true,
              },
            ]
          : [
              {
                model: 'GeneralNotify',
                field: 'Date',
                asc: false,
              },
              {
                model: 'GeneralNotify',
                field: 'Name',
                filter: [''],
                isRegex: true,
              },
            ];
        if (!isCustomer) {
          if (!showTypeNotifications) {
            filters.push({
              model: 'Notify',
              field: 'Mail',
              filter: [mail],
            });
          } else {
            filters.push({
              model: 'GeneralNotify',
              field: 'Mail',
              filter: [mail],
            });
          }
        }
        const {
          data: { data },
        } = !showTypeNotifications
          ? await getNotificationsData({
              data: {
                filters,
                reduce: {
                  limit: 0,
                  skip: 0,
                },
              },
            })
          : await getNotificationsMassiveData({
              data: {
                filters,
                reduce: {
                  limit: 0,
                  skip: 0,
                },
              },
            });
        if (data.length === 0) {
          setNotifyList([]);
        }
        if (data.length > 0) {
          setNotifyList(data);
        }
      } catch (error) {
        setNotifyList([]);
      }
    }
  };

  const markAsReadLocally = (notificationId) => {
    const index = notifyList.findIndex((notification) => notification.id === notificationId);
    notifyList[index].numAnswersUnread = 0;
    setNotifyList(notifyList);
  };

  const updateNotification = (notification) => {
    const notificationList = [...notifyList];
    const index = notificationList.findIndex((n) => n.id === notification.id);
    notificationList[index] = notification;
    setNotifyList(notificationList);
  };

  const handleNotificationChange = async (notification) => {
    const currentNotificationUpdated = await getNotificationById(notification.id);
    handleSelectNotificationReview(currentNotificationUpdated);
    updateNotification(currentNotificationUpdated);
    markAsReadLocally(currentNotificationUpdated.id);
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  return (
    <>
      <div className="d-flex justify-content-between panel-title-container">
        <LabelIcon
          label={t('notifications_communication_title')}
          Icon={() => <ConversationIcon fill={colors.white} className="label-icon__notification" />}
          color={mainColor}
          className="ml-1 label-icon text-uppercase"
          classNameMain="mb-3"
        />
        <ButtonLabelIcon
          id="add-icon-notification"
          text={t('general_button_new')}
          type="button"
          Icon={() => <AddIcon fill={colors.black} />}
          className="button-icon mr-1"
          onClick={() => {
            setOpenFormModal(true);
          }}
        />
      </div>
      <SearchNotifyDiv className="d-flex mb-2 form">
        <SearchClients
          Icon={() => null}
          borderRadius={2}
          className=""
          inputClassName="w-100"
          buttonClassName=""
          withButtonClear
          rightIcon={<SearchIcon fill={colors.black} />}
          onChange={searchNotifications}
          value={searchValue ?? ''}
          placeholder={t('notification_search')}
        />
      </SearchNotifyDiv>
      <Ul className="list-unstyled" isCurrentNotification={currentNotificationReview}>
        {!(loading || massiveLoading) ? (
          <>
            {notifyList.map((notify, i) => (
              <Li
                key={notify.id}
                onClick={() => handleNotificationChange(notify)}
                selectedColor={
                  currentNotificationReview?.id === notify.id ? colors.whiteSmoke : colors.white
                }
              >
                <Div numAnswersUnread={notify.numAnswersUnread}>
                  <div className="notification-name-container mb-1" id={`notify-name-${i}`}>
                    <UncontrolledTooltip
                      placement="top"
                      target={`notify-name-${i}`}
                      autohide={false}
                    >
                      {notify.name}
                    </UncontrolledTooltip>
                    <p className="notification-name">{notify.name}</p>
                  </div>
                  <span className="notification-date-time">
                    {moment(notify.date).format('DD/MM/YYYY HH:mm')}
                  </span>
                </Div>
              </Li>
            ))}
          </>
        ) : (
          <div className="d-flex justify-content-center mb-5 align-items-center h-100">
            <Spinner color={mainColor} />
          </div>
        )}
      </Ul>
      {!notifyList.length && (
        <div className="d-flex flex-column justify-content-center align-items-center chat-notifications-not-found">
          <NotificationIcon fill={colors.suvaGrey} />
          <p>{t('notification_not_found')}</p>
        </div>
      )}
      <AddFormModal isOpen={openFormModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
        />
        <div className="title-wrapper">
          <h1 className="title">{t('notification_title_add')}</h1>
        </div>
        <ModalBody>
          <AddNotify onCancel={onCloseFormModal} isDetailCommunication onCreate={onCreate} />
        </ModalBody>
      </AddFormModal>
    </>
  );
};

const Div = styled.div`
  width: 100%;
  height: 61px;
  cursor: pointer;
  padding: 10px;
  .notification-name-container {
    font-family: ${({ numAnswersUnread }) => (numAnswersUnread > 0 ? fonts.bold : fonts.default)};
  }
`;

const Li = styled.li`
  background: ${({ selectedColor }) => selectedColor};
  border-bottom: 1px solid ${colors.pattens};
  border-top: 1px solid ${colors.pattens};
  &:hover {
    background: ${colors.whiteSmoke};
  }
`;

export default Notifications;
